import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {
	InfinityListColumn,
	InfinityListColumnFilterOptions,
	InfinityListColumnMetaData,
} from 'components/InfinityList/InfinityList';
import {queryStringMongoArraySTD, queryStringMongoDateSTD} from 'components/InfinityList/InfinitListStandardHooks';
import {renderArrayField, renderDate} from 'components/EntityList/ColumnRenderers';
import {GetexRegulationsQueryQuery} from './hooks/GetexRegulations.generated';
import {GetexRegulationModified} from './GetexRegulationPage';
import {
	LinkCompat as FluentLink,
	StackCompat as Stack,
} from 'components/FluentUICompat';

export const useGetexRegulationColumns = () => {
	const {t} = useTranslation('features/getexregulation', {
		keyPrefix: 'GetexRegulationPage',
	});

	const columns: InfinityListColumn[] = useMemo(
		() => [
			{
				key: 'regulationNumber',
				name: t('RegulationNumber'),
				fieldName: 'regulationNumber',
				minWidth: 150,
				maxWidth: 200,
				sortable: true,
				isResizable: true,
				filterOnFilter: true,
				filterType: 'Array',
			},
			{
				key: 'regulationTitle',
				name: t('RegulationTitle'),
				fieldName: 'regulationTitle',
				minWidth: 300,
				maxWidth: 600,
				sortable: true,
				isResizable: true,
				isMultiline: true,
				filterOnFilter: true,
				filterType: 'Array',
			},
			{
				key: 'regulationStatus',
				name: t('RegulationStatus'),
				fieldName: 'regulationStatus',
				minWidth: 120,
				maxWidth: 160,
				sortable: true,
				isResizable: true,
				filterOnFilter: true,
				filterType: 'Array',
			},
			{
				key: 'dateEffective',
				name: t('DateEffective'),
				fieldName: 'dateEffective',
				minWidth: 120,
				maxWidth: 150,
				sortable: true,
				isResizable: true,
				filterType: 'Date',
				onRender: renderDate(),
			},
			{
				key: 'keywords',
				name: t('Keywords'),
				fieldName: 'keywords',
				minWidth: 200,
				maxWidth: 400,
				isResizable: true,
				isMultiline: true,
				filterOnFilter: true,
				filterType: 'Array',
				onRender: renderArrayField(),
			},
			{
				key: 'vehicleCategories',
				name: t('VehicleCategories'),
				fieldName: 'vehicleCategories',
				minWidth: 150,
				maxWidth: 300,
				isResizable: true,
				isMultiline: true,
				filterOnFilter: true,
				filterType: 'Array',
				onRender: renderArrayField(),
			},
			{
				key: 'attachments',
				name: t('AttachmentFileNames'),
				fieldName: 'attachments',
				minWidth: 200,
				maxWidth: 400,
				isResizable: true,
				isMultiline: true,
				onRender: (item: GetexRegulationModified) => (
					<Stack tokens={{childrenGap: 4}}>
						{item.attachments.map((attachment, i) => {
							const displayName =
								attachment.file.originalFileName ??
								attachment.file.fileName ??
								attachment.attachmentId;
							const href = `${attachment.file.uri}/${attachment.file.originalFileName}`;
							const isPdf =
								attachment.file.originalFileName
									?.toLowerCase()
									.endsWith('.pdf') ?? false;
							return (
								<FluentLink
									key={i}
									href={href}
									target='_blank'
									download={false}
									onClick={
										isPdf
											? e => {
													e.preventDefault();
													fetch(href)
														.then(res => res.blob())
														.then(blob => {
															const blobUrl = URL.createObjectURL(
																new Blob([blob], {type: 'application/pdf'}),
															);
															window.open(blobUrl, '_blank');
														});
											  }
											: undefined
									}
								>
									{displayName}
								</FluentLink>
							);
						})}
					</Stack>
				),
			},
		],
		[t],
	);

	return columns;
};

export const useGetexRegulationColumnMetaData =
	(): InfinityListColumnMetaData[] => [
		{
			key: 'regulationNumber',
			mongoDBFn: queryStringMongoArraySTD(),
		},
		{
			key: 'regulationTitle',
			mongoDBFn: queryStringMongoArraySTD(),
		},
		{
			key: 'regulationStatus',
			mongoDBFn: queryStringMongoArraySTD(),
		},
		{
			key: 'dateEffective',
			mongoDBFn: queryStringMongoDateSTD(),
		},
		{
			key: 'keywords',
			mongoDBFn: queryStringMongoArraySTD(),
		},
		{
			key: 'vehicleCategories',
			mongoDBFn: queryStringMongoArraySTD(),
		},
	];

export const useGetexRegulationFilterOptions = (
	dataQuery: GetexRegulationsQueryQuery | undefined,
): InfinityListColumnFilterOptions[] => {
	const items = dataQuery?.getexRegulationsQuery.getexRegulationsBatch ?? [];

	const uniqueValues = (field: keyof GetexRegulationModified) =>
		Array.from(new Set(items.map(x => String(x[field] ?? '')))).map(v => ({
			optionKey: v,
			name: v,
		}));

	const uniqueKeywords = Array.from(
		new Set(items.flatMap(x => x.keywords ?? [])),
	).map(v => ({optionKey: v, name: v}));

	const uniqueVehicleCategories = Array.from(
		new Set(items.flatMap(x => x.vehicleCategories ?? [])),
	).map(v => ({optionKey: v, name: v}));

	return [
		{key: 'regulationNumber', fieldValuesUnique: uniqueValues('regulationNumber')},
		{key: 'regulationTitle', fieldValuesUnique: uniqueValues('regulationTitle')},
		{key: 'regulationStatus', fieldValuesUnique: uniqueValues('regulationStatus')},
		{key: 'keywords', fieldValuesUnique: uniqueKeywords},
		{key: 'vehicleCategories', fieldValuesUnique: uniqueVehicleCategories},
	];
};
